import React from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { DerinOrganization, DerinUserRole, MembershipRequest } from "../../../api/derin";
import { collectionColors, fieldLabelSx, outlinedButtonSx, subtitleSx } from "../CollectionsTab/tokens";
import { organizationLabel, ROLE_OPTIONS, selectSx } from "./styles";

interface Props {
  rows: MembershipRequest[];
  onChange: (rows: MembershipRequest[]) => void;
  /** Organizations that can be picked. */
  organizations: DerinOrganization[];
  /** Organizations the user already belongs to; never offered. */
  excludedOrganizationIds?: string[];
  /** One fixed organization (adding from an organization's page). */
  lockedOrganization?: DerinOrganization;
  description: string;
}

export const emptyRow = (organizationId = ""): MembershipRequest => ({ organizationId, role: "Viewer" });

/**
 * The "Organization access" block of the add-user modal: one Organization + Role pair
 * per row, and "+ Add Organization" for more. Each organization can be picked once.
 */
const OrganizationAccessRows: React.FC<Props> = ({
  rows,
  onChange,
  organizations,
  excludedOrganizationIds = [],
  lockedOrganization,
  description,
}) => {
  const available = organizations.filter((o) => !excludedOrganizationIds.includes(o.id));
  const chosen = rows.map((r) => r.organizationId).filter(Boolean);
  const canAddRow = !lockedOrganization && chosen.length === rows.length && rows.length < available.length;

  const update = (index: number, patch: Partial<MembershipRequest>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <Typography sx={{ ...fieldLabelSx, fontWeight: 600 }}>Organization access</Typography>
        <Typography sx={{ ...subtitleSx, fontSize: "13px" }}>{description}</Typography>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 140px 24px", gap: "8px 12px", alignItems: "center" }}>
        <Typography sx={fieldLabelSx}>Organization</Typography>
        <Typography sx={fieldLabelSx}>Role</Typography>
        <span />
        {rows.map((row, index) => (
          <React.Fragment key={index}>
            <Box
              component="select"
              aria-label={`Organization ${index + 1}`}
              value={row.organizationId}
              disabled={!!lockedOrganization}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => update(index, { organizationId: e.target.value })}
              sx={selectSx}
            >
              {lockedOrganization ? (
                <option value={lockedOrganization.id}>{lockedOrganization.name}</option>
              ) : (
                <>
                  <option value="">Select</option>
                  {available
                    .filter((o) => o.id === row.organizationId || !chosen.includes(o.id))
                    .map((o) => (
                      <option key={o.id} value={o.id}>
                        {organizationLabel(o.id, o.name, organizations)}
                      </option>
                    ))}
                </>
              )}
            </Box>
            <Box
              component="select"
              aria-label={`Role ${index + 1}`}
              value={row.role}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                update(index, { role: e.target.value as DerinUserRole })
              }
              sx={selectSx}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Box>
            {rows.length > 1 ? (
              <IconButton
                size="small"
                aria-label={`Remove organization ${index + 1}`}
                onClick={() => onChange(rows.filter((_, i) => i !== index))}
                sx={{ padding: "2px", color: collectionColors.textSecondary }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : (
              <span />
            )}
          </React.Fragment>
        ))}
      </Box>

      {!lockedOrganization && (
        <Button
          onClick={() => onChange([...rows, emptyRow()])}
          disabled={!canAddRow}
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          sx={{ ...outlinedButtonSx, width: "100%", justifyContent: "center" }}
        >
          Add Organization
        </Button>
      )}
    </Box>
  );
};

export default OrganizationAccessRows;
